const TOGGLE_RETRY_MS = 500;
const MAX_WAIT_MS = 30_000;
const LOG_PREFIX = '[cue:send]';

/**
 * Send a message to an already-open Cue panel. Returns false when the UMD
 * instance is missing, so the caller can open the panel first.
 */
export function sendNow(data: string, mountId: string): boolean {
  const instance = window.WxCopilotComponentInstances?.[mountId];
  if (!instance?.sendMessageToQueue) {
    // eslint-disable-next-line no-console
    console.error(`${LOG_PREFIX} sendMessageToQueue unavailable`);
    return false;
  }

  instance.sendMessageToQueue({ type: 'user', data });
  return true;
}

/**
 * Open Cue and send `data` once the panel confirms it is open.
 *
 * `toggleCopilotPanel` no-ops while the UMD bundle is still initializing, so
 * this retries until `wx-copilot-panel { isOpen: true }` fires. Gives up after
 * MAX_WAIT_MS. Assumes the panel is closed — toggling an open panel closes it.
 *
 * @returns cleanup that cancels timers and the panel listener.
 */
export function openCueAndSend(
  data: string,
  opts: { mountId: string; onSent?: () => void },
): () => void {
  const { mountId, onSent } = opts;
  let sent = false;
  let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let maxWaitTimeoutId: ReturnType<typeof setTimeout> | null = null;

  function onPanel(event: Event) {
    const detail = (event as CustomEvent<{ isOpen?: boolean }>).detail;
    if (sent || !detail?.isOpen) return;

    sent = true;
    cleanup();
    sendNow(data, mountId);
    onSent?.();
  }

  function cleanup() {
    window.removeEventListener('wx-copilot-panel', onPanel);
    if (retryTimeoutId !== null) {
      clearTimeout(retryTimeoutId);
      retryTimeoutId = null;
    }
    if (maxWaitTimeoutId !== null) {
      clearTimeout(maxWaitTimeoutId);
      maxWaitTimeoutId = null;
    }
  }

  function tryToggle() {
    if (sent) return;

    const instance = window.WxCopilotComponentInstances?.[mountId];
    if (instance?.toggleCopilotPanel) {
      try {
        instance.toggleCopilotPanel();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`${LOG_PREFIX} toggleCopilotPanel threw`, err);
      }
    }

    retryTimeoutId = setTimeout(tryToggle, TOGGLE_RETRY_MS);
  }

  window.addEventListener('wx-copilot-panel', onPanel);
  tryToggle();
  maxWaitTimeoutId = setTimeout(() => {
    if (!sent) {
      // eslint-disable-next-line no-console
      console.warn(`${LOG_PREFIX} gave up after ${MAX_WAIT_MS}ms`);
      cleanup();
    }
  }, MAX_WAIT_MS);

  return cleanup;
}
